import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { fadeInUp400ms } from "src/@vex/animations/fade-in-up.animation";
import { stagger40ms } from "src/@vex/animations/stagger.animation";
import {
  FileWithRelations,
  ItemPublicationWithRelations,
  ItemTitleWithRelations,
  ItemTypeWithRelations,
  ItemWithRelations,
  LocationWithRelations,
  MarkingPlace,
  MarkingTypeWithRelations,
  SignaturePlace,
  UserInventoryPermission,
} from "src/app/api/models";
import { LightboxComponent } from "../../lightbox/lightbox.component";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { lastValueFrom } from "rxjs";
import { AuthService } from "src/app/core/services/auth.service";
import { InventoryService } from "src/app/_services/inventory.service";
import { MarkingService } from "src/app/_services/marking.service";
import { PictureService } from "src/app/_services/picture.service";
import { SignatureService } from "src/app/_services/signature.service";
import {
  AdminItemsControllerService,
  SignaturePlaceControllerService,
  MarkingPlaceControllerService,
  MarkingTypeControllerService,
  ItemTypeControllerService,
} from "src/app/api/services";
import { AuthorService } from "src/app/shared/services/author.service";
import { LocationService } from "src/app/shared/services/location.service";
import { SanitizeService } from "src/app/shared/services/sanitize.service";
import { REMOVAL_TYPE_LABELS } from "src/app/_services/item.service";
import { HtmlContentDialogComponent } from "../../html-content-dialog/html-content-dialog.component";

interface ItemPublicationInfo {
  title: string;
  author: string;
  itemInfo: ItemPublicationWithRelations;
}

interface ItemDetailPermissions {
  role: "owner" | "manager" | "curator" | "viewer" | "guest";
  showPhotos: "all" | "publicOnly" | "none";
  allowEdit: boolean;
}

const ROLES_PERMISSIONS: ItemDetailPermissions[] = [
  { role: "owner", showPhotos: "all", allowEdit: true },
  { role: "manager", showPhotos: "all", allowEdit: true },
  { role: "curator", showPhotos: "all", allowEdit: true },
  { role: "viewer", showPhotos: "publicOnly", allowEdit: false },
  { role: "guest", showPhotos: "publicOnly", allowEdit: false },
];

@Component({
  selector: "app-item-preview",
  templateUrl: "./item-preview.component.html",
  styleUrls: ["./item-preview.component.scss"],
  animations: [fadeInUp400ms, stagger40ms],
  standalone: false,
})
export class ItemPreviewComponent implements OnInit {
  @ViewChild(LightboxComponent) lightbox: LightboxComponent;
  loading: boolean = false;
  readonly removalTypeLabels = REMOVAL_TYPE_LABELS;

  item: ItemWithRelations;
  inventoryLocations: LocationWithRelations[];
  showAuthor: boolean;
  hideEditButton: boolean = false;
  otherTitles: ItemTitleWithRelations[];
  showItemDetailCallback: (id: string) => void;

  currentImageIndex: number = 0;
  images: FileWithRelations[] = [];

  signaturePlaces: SignaturePlace[] = [];
  markingPlaces: MarkingPlace[] = [];
  markingTypes: MarkingTypeWithRelations[] = [];
  itemTypes: ItemTypeWithRelations[] = [];

  // Parsed Values
  dimensions: string = "";
  dating: string = "";
  technique: string = "";
  substrate: string = "";
  itemType: string = "";
  locationString: string = "";

  itemPublications: ItemPublicationInfo[] = [];

  //General Tab Props
  showTitleCard: boolean = false;
  showOtherTitlesCard: boolean = false;
  showInventoryCard: boolean = false;
  showPropertyCard: boolean = false;
  showPhotoMode: "all" | "publicOnly" | "none" = "none";

  currentUserItemDetailPermissions: ItemDetailPermissions;

  constructor(
    public dialogRef: MatDialogRef<ItemPreviewComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      item: ItemWithRelations;
      showAuthor?: boolean;
      hideEditButton?: boolean;
      reloadItem?: boolean;
      allowNotPublicPhoto?: boolean;
      showItemDetailCallback?: (id: string) => void;
    },
    private itemService: AdminItemsControllerService,
    public authorService: AuthorService,
    public pictureService: PictureService,
    private signaturePlacesService: SignaturePlaceControllerService,
    private markingPlacesService: MarkingPlaceControllerService,
    private markingTypesService: MarkingTypeControllerService,
    private itemTypesService: ItemTypeControllerService,
    private router: Router,
    public sanitizeService: SanitizeService,
    public _signatureService: SignatureService,
    public _markingService: MarkingService,
    public locationService: LocationService,
    private authService: AuthService,
    private inventoryService: InventoryService,
    private dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.hideEditButton = this.data.hideEditButton;
    this.showItemDetailCallback =
      this.data.showItemDetailCallback || (() => {});

    try {
      const currentUserInventoryPermissions =
        this.authService.currentUserValue.inventoryPermissions?.find(
          (a) => a.inventoryId === this.data.item.inventoryId,
        ) as UserInventoryPermission;

      this.currentUserItemDetailPermissions = ROLES_PERMISSIONS.find(
        (a) => a.role === currentUserInventoryPermissions.role["code"],
      );
    } catch (error) {
      console.error("Error while getting user permissions", error);
      this.currentUserItemDetailPermissions = ROLES_PERMISSIONS.find(
        (a) => a.role === "guest",
      );
    }

    this.signaturePlaces = await lastValueFrom(
      this.signaturePlacesService.getSignaturePlacesList(),
    );
    this.markingPlaces = await lastValueFrom(
      this.markingPlacesService.getMarkingPlacesList(),
    );
    this.markingTypes = await lastValueFrom(
      this.markingTypesService.getMarkingTypesList(),
    );
    this.itemTypes = await lastValueFrom(
      this.itemTypesService.getItemTypesList(),
    );

    this.item = this.data.item;
    console.log("Item", this.item);

    if (this.data.reloadItem) {
      this.item = await lastValueFrom(
        this.itemService.adminFindById({ itemId: this.item.id }),
      );
    }

    this.showPhotoMode = this.currentUserItemDetailPermissions.showPhotos;
    this.showAuthor = this.data.showAuthor;
    this.otherTitles = this.item.titles ? this.item.titles : [];
    const publicOnly = this.showPhotoMode === "publicOnly" ? true : false;
    this.images =
      this.showPhotoMode !== "none"
        ? this.getItemPictures(this.item, publicOnly)
        : [];

    this.dimensions = this.parseItemDimensions(this.item);
    this.dating = this.parseItemDating(this.item);
    this.technique = this.parseItemTechnique(this.item);
    this.substrate = this.parseItemSubstrate(this.item);
    this.itemType = this.parseItemType(this.item);
    this.itemPublications = this.getItemPublications(this.item);
    if (this.item.location) {
      this.inventoryLocations = this.locationService.flattenLocations(
        this.inventoryService.getLocations(),
      );
      this.locationString = this.item.location
        ? `${this.locationService.getLocationLabel(
            this.item.location,
            this.inventoryLocations,
          )}${
            this.item.location.description
              ? `- ${this.item.location.description}`
              : ""
          } `
        : "";
    }

    this.showTitleCard =
      this.item.authors ||
      this.item.translations[0].title ||
      this.item.translations[1].title ||
      this.item.originalTitle
        ? true
        : false;
    this.showOtherTitlesCard = this.otherTitles?.length > 0 ? true : false;

    this.showInventoryCard =
      this.item.inventoryCode || this.item.itemTypeId || this.item.location
        ? true
        : false;
    this.showPropertyCard =
      this.dimensions ||
      this.dating ||
      this.technique ||
      this.item.extras?.length > 0 ||
      this.item.signatures?.length > 0 ||
      this.item.markings?.length > 0
        ? true
        : false;

    this.loading = false;
  }

  /**
   * Checks if a string contains only empty characters, <p> tags, HTML entities like &nbsp;, and newline characters.
   * @param str The string to check.
   * @returns True if the string contains only empty characters, <p> tags, HTML entities like &nbsp;, and newline characters, false otherwise.
   */
  isEmpty(str: string): boolean {
    // Regular expression to match strings that contain only <p> tags, empty characters, HTML entities like &nbsp;, and newline characters
    const regex = /^(\s*<p>\s*(&nbsp;|\s)*<\/p>\s*)*$/;

    // Check if the string is empty
    if (str.length === 0) {
      return true;
    }

    // Test the string against the regular expression
    return regex.test(str);
  }

  /**
   * Attempts to open a quick item detail dialog by invoking the provided callback.
   *
   * This method checks whether the showItemDetailCallback function is defined and of type "function".
   * If valid, it calls the callback with the provided item id and then closes the current dialog.
   * If not, it logs an error message for debugging purposes.
   *
   * @param id - The unique identifier of the ItemWithRelations instance to open.
   */
  prepareItemQuickDialog(id: string) {
    // Verify that showItemDetailCallback is defined and is a function.
    if (
      this.showItemDetailCallback &&
      typeof this.showItemDetailCallback === "function"
    ) {
      // Invoke the callback function passing the item id so that the parent can open the corresponding item detail.
      this.showItemDetailCallback(id);
      // After invoking the callback, close the current dialog.
      this.close();
    } else {
      // Log an error if the callback is either undefined or not a function.
      console.error("showItemDetailCallback is not defined or not a function");
    }
  }

  /**
   * Navigates to the item detail page and closes the current dialog.
   *
   * @param {string} id - The ID of the item to navigate to.
   */
  async goToItemDetail(id: string) {
    // Navigate to the item detail page using the router
    await this.router.navigate(["/item", id]).then(() => {
      // Close the current dialog after navigation
      this.close();
    });
  }

  private parseItemDimensions(
    item: ItemWithRelations,
    addUnits: boolean = true,
  ): string {
    let dimensions = "";
    const dimensionsArray: number[] = [];
    dimensionsArray.push(item.dimensionHeight ? item.dimensionHeight : 0);
    dimensionsArray.push(this.item.dimensionWidth ? item.dimensionWidth : 0);
    dimensionsArray.push(item.dimensionDepth ? item.dimensionDepth : 0);

    dimensionsArray.forEach((dimension, index) => {
      if (index !== 0 && dimension !== 0 && dimensions !== "") {
        dimensions += " × ";
      }
      dimensions += dimension !== 0 ? `${dimension}` : "";
    });

    if (!item.dimensionWidth && item.dimensionHeight && !item.dimensionDepth) {
      dimensions = `v. ${dimensions}`;
    }

    if (dimensionsArray.findIndex((a) => a !== 0) !== -1 && addUnits) {
      dimensions += " cm";
    }

    return dimensions;
  }

  private parseItemDating(item: ItemWithRelations): string {
    let dating = "";

    if (item.translations[0].dating) {
      dating = item.translations[0].dating;
    } else if (item.datingOrder) {
      dating = item.datingOrder.toString();
    } else {
      dating = item.translations[1].dating;
    }

    return dating;
  }

  private parseItemTechnique(item: ItemWithRelations): string {
    let technique = "";
    if (item.translations[0].technique) {
      technique = item.translations[0].technique;
    } else if (item.translations[1].technique) {
      technique = item.translations[1].technique;
    }
    return technique;
  }

  private parseItemSubstrate(item: ItemWithRelations): string {
    let substrate = "";
    if (item.translations[0].substrate) {
      substrate = item.translations[0].substrate;
    } else if (item.translations[1].substrate) {
      substrate = item.translations[1].substrate;
    }
    return substrate;
  }

  private parseItemType(item: ItemWithRelations): string {
    let itemType = "";

    const itemTypeObject = this.itemTypes.find((a) => a.id === item.itemTypeId);
    if (itemTypeObject) {
      itemType = `${itemTypeObject.langs[0].name} (${itemTypeObject.shortcut})`;
    }

    return itemType;
  }

  getItemPublications(item: ItemWithRelations): ItemPublicationInfo[] {
    const publications: ItemPublicationInfo[] = [];
    item.publications?.forEach((publication) => {
      publications.push({
        title: publication.originalTitle,
        author: publication.author,
        itemInfo: item.itemPublications.find(
          (a) => a.publicationId === publication.id,
        ),
      });
    });
    return publications;
  }

  getItemPictures(
    item: ItemWithRelations,
    publicOnly: boolean = false,
  ): FileWithRelations[] {
    return this.pictureService.getAllPictures(item, publicOnly);
  }

  openRemovalNoteDialog(): void {
    this.dialog.open(HtmlContentDialogComponent, {
      data: { title: "Poznámka k odebrání", html: this.item.removalNote },
      maxWidth: "600px",
      width: "100%",
    });
  }

  edit(): void {
    this.router.navigate(["/item", this.item.id]);
    this.close(); // close dialog
  }

  close(): void {
    this.dialogRef.close();
  }
}
